import React, { useState, useEffect } from "react";
import AntEditor from "../editor/anteditor/anteditor";
import { db, updateJournalDay } from "../../firebase/firebase.utils";
import useImageUpload from "../../hooks/useImageUpload";
import UploadOverlay from "../upload/UploadOverlay";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { IoChevronBackOutline } from "react-icons/io5";
import { BsFullscreenExit } from "react-icons/bs";
import "./journal.styles.scss";

const dateKey = (date) =>
  `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;

export default function Journal({
  currentUser,
  display,
  fullscreen,
  setJournalFullscreen,
}) {
  const [pickerDate, setPickerDate] = useState(() => new Date());
  const selectedDate = dateKey(pickerDate);
  const entryKey = `${currentUser?.uid}/${selectedDate}`;
  const [loadedEntry, setLoadedEntry] = useState("");
  const [loadError, setLoadError] = useState("");
  const [initialValue, setInitialValue] = useState("");
  const [journalContent, setJournalContent] = useState("");
  const [docImgFile, setDocImgFile] = useState(null);
  const [docImgFileAltText, setDocImgFileAltText] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const { uploading, progress, status, uploadImage } = useImageUpload({
    userId: currentUser?.uid,
    getBasePath: () =>
      currentUser?.uid ? `users/${currentUser.uid}/journal` : "",
    getParentPath: () => `users/${currentUser.uid}/journals/${selectedDate}`,
  });

  useEffect(() => {
    if (!currentUser?.uid) return;
    let active = true;
    setLoadError("");
    setDocImgFile(null);
    const unsubscribe = db
      .collection("users")
      .doc(currentUser.uid)
      .collection("journals")
      .doc(selectedDate)
      .onSnapshot(
        (snapshot) => {
          if (!active) return;
          const text = snapshot.exists ? snapshot.data().journal || "" : "";
          setInitialValue(text);
          setJournalContent(text);
          setLoadedEntry(entryKey);
        },
        (error) => {
          if (active)
            setLoadError(error.message || "Could not load this journal entry.");
        },
      );
    return () => {
      active = false;
      unsubscribe();
    };
  }, [currentUser?.uid, selectedDate, entryKey]);

  const handleSaveForm = async () => {
    if (
      !currentUser?.uid ||
      isSaving ||
      uploading ||
      loadedEntry !== entryKey ||
      loadError
    )
      return;
    try {
      setIsSaving(true);
      await updateJournalDay(currentUser.uid, selectedDate, journalContent);
    } catch (error) {
      window.alert(error.message || "Your journal could not be saved.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleUploadDocImage = async () => {
    const { url } = await uploadImage(docImgFile);
    return url;
  };

  return (
    <div
      className="journal"
      id="journal"
      style={{
        display,
        height: fullscreen ? "calc(100% - 30px)" : "100%",
        borderRadius: fullscreen ? "5px 5px 5px 5px" : "0px 5px 5px 5px",
      }}
      onClick={(event) => event.stopPropagation()}
    >
      <UploadOverlay visible={uploading} progress={progress} status={status} />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: fullscreen ? "space-between" : "flex-end",
          width: "100%",
        }}
      >
        {fullscreen && <h3>Journal</h3>}
        <div className="journal__btn-group">
          <button
            disabled={uploading || isSaving}
            onClick={() => {
              const previous = new Date(pickerDate);
              previous.setDate(previous.getDate() - 1);
              setPickerDate(previous);
            }}
          >
            <IoChevronBackOutline />
          </button>
          <button
            className="journal__datepicker"
            disabled={uploading || isSaving}
          >
            <DatePicker
              popperProps={{ strategy: "fixed" }}
              wrapperClassName="datepicker"
              placeholderText="..."
              value={pickerDate}
              timeFormat="p"
              dateFormat="dd"
              selected={pickerDate}
              maxDate={new Date()}
              disabled={uploading || isSaving}
              onChange={(date) => {
                if (date) setPickerDate(date);
              }}
            />
          </button>
          <button
            disabled={uploading || isSaving}
            onClick={() => setPickerDate(new Date())}
            className={
              pickerDate.toDateString() === new Date().toDateString()
                ? "journal__today-btn"
                : "journal__today-btn-passive"
            }
          >
            Today
          </button>
          {fullscreen && (
            <button
              disabled={uploading}
              onClick={() => setJournalFullscreen(false)}
              className="notes__form-fullscreen-btn"
              style={{ background: "unset" }}
            >
              <BsFullscreenExit
                size="1.6em"
                data-tooltip-id="turnOnFullScreen-how"
                aria-label="Close"
                style={{ marginLeft: 5, marginRight: 5 }}
              />
            </button>
          )}
        </div>
      </div>
      {loadError ? (
        <p role="alert">{loadError}</p>
      ) : loadedEntry === entryKey ? (
        <AntEditor
          id="journalNote"
          key={entryKey}
          mainToolbarOn={fullscreen}
          floatingToolbarOn={true}
          placeholder="Write down some ideas, memories, feelings, quick notes, etc..."
          initialValue={initialValue}
          setDocContent={setJournalContent}
          setDocImgFile={setDocImgFile}
          setDocImgFileAltText={setDocImgFileAltText}
          docImgFileAltText={docImgFileAltText}
          handleUploadDocImage={handleUploadDocImage}
          editability={true}
        />
      ) : (
        <p>Loading journal…</p>
      )}
        <div className="journal__form-button" onClick={handleSaveForm} style={{bottom: fullscreen ? 15 : 15}}>
          {isSaving ? "Saving..." : "Save"}
        </div>
    </div>
  );
}
